import React, { useEffect, useState } from "react";
import { ReactComponent as HeaderLogo } from "../assets/logo-hover.svg";
import styles from "./Header.module.css";
import AccountMenu, { MenuItem } from "./AccountMenu";
import HeaderQBankLink from "./HeaderQBankLink";
import { Link, useLocation } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import {
  HomeIcon,
  BookmarkSquareIcon,
  Squares2X2Icon,
  ChatBubbleLeftEllipsisIcon,
  BanknotesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { authenticatedFetch } from "../utils/authenticatedFetch";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

interface HeaderProps {
  className?: string;
  language?: "en" | "ar";
}

const Header: React.FC<HeaderProps> = ({ className = "", language }) => {
  const { session, user, loading } = useAuthContext();
  const location = useLocation();
  const isArabic = language === "ar";
  const labels = isArabic
    ? {
        home: "الرئيسية",
        account: "حسابي",
        chat: "الدردشة",
        qbank: "بنك أسئلة SDLE",
        pricing: "الباقات",
        supervisor: "الإشراف",
        contact: "تواصل معنا",
        login: "تسجيل الدخول",
        getStarted: "ابدأ الآن",
        logo: "شعار C-Shaped",
      }
    : {
        home: "Home",
        account: "My Account",
        chat: "Chat",
        qbank: "SDLE QBank",
        pricing: "Pricing",
        supervisor: "Supervisor",
        contact: "Contact",
        login: "Log in",
        getStarted: "Get Started",
        logo: "C-Shaped Logo",
      };
  const [isAuthVisible, setIsAuthVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check supervisor status when user changes
  useEffect(() => {
    if (!user?.id) {
      setIsSupervisor(false);
      return;
    }
    const checkSupervisor = async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/auth/is-supervisor/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setIsSupervisor(data.is_supervisor === true);
        }
      } catch {
        setIsSupervisor(false);
      }
    };
    checkSupervisor();
  }, [user?.id]);

  // Effect to handle smooth transition
  useEffect(() => {
    if (!loading) {
      // Small timeout to Ensure smooth transition and prevent flash
      const timer = setTimeout(() => setIsAuthVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsAuthVisible(false);
    }
  }, [loading]);

  // Build a default set of menu items then exclude the current page
  const baseMenuItems: MenuItem[] = [
    { label: isArabic ? "ملاحظاتي" : "Notes", icon: <BookmarkSquareIcon width={20} height={20} />, to: "/notes" },
    {
      label: labels.home,
      icon: <HomeIcon width={20} height={20} />,
      to: "/",
    },
    {
      label: labels.account,
      icon: <Squares2X2Icon width={20} height={20} />,
      to: "/dashboard",
    },
    {
      label: labels.chat,
      icon: <ChatBubbleLeftEllipsisIcon width={20} height={20} />,
      to: "/chat",
    },
    {
      label: labels.pricing,
      icon: <BanknotesIcon width={20} height={20} />,
      to: "/pricing",
    },
    ...(isSupervisor ? [{
      label: labels.supervisor,
      icon: <UserGroupIcon width={20} height={20} />,
      to: "/supervisor",
    }] : []),
  ];

  const menuItems = baseMenuItems.filter((item) => item.to !== location.pathname);

  // Helper to determine if a link is active
  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={[styles.header, scrolled ? styles.headerScrolled : "", className]
        .filter(Boolean)
        .join(" ")}
      dir={language ? (isArabic ? "rtl" : "ltr") : undefined}
      lang={language}
    >
      <div className={styles.headerContainer}>
        {/* Logo */}
        <Link to="/">
          <HeaderLogo role="img" aria-label={labels.logo} className={`cshaped-signature ${styles.headerLogo}`} width="140" height="42" />
        </Link>

        {/* Right side navigation */}
        <nav className={styles.headerRight}>
          {/* Navigation links - visible on desktop */}
          <Link
            to="/chat"
            className={`${styles.navLink} ${isActive('/chat') ? styles.navLinkActive : ''}`}
          >
            {labels.chat}
          </Link>
          <div className={styles.desktopNavAction}>
            <HeaderQBankLink
              isActive={location.pathname.startsWith("/qbank/sdle")}
              label={language ? labels.qbank : undefined}
              to="/qbank/sdle"
            />
            <HeaderQBankLink
              isActive={location.pathname.startsWith("/qbank/smle") || location.pathname.startsWith("/ar/qbank/smle")}
              label={isArabic ? "بنك أسئلة SMLE" : "SMLE QBank"}
              to={isArabic ? "/ar/qbank/smle" : "/qbank/smle"}
            />
          </div>
          <Link
            to="/pricing"
            className={`${styles.navLink} ${isActive('/pricing') ? styles.navLinkActive : ''}`}
          >
            {labels.pricing}
          </Link>
          <Link
            to="/pricing"
            className={`${styles.mobilePricingLink} ${isActive('/pricing') ? styles.mobilePricingLinkActive : ''}`}
          >
            {labels.pricing}
          </Link>
          <a
            href="https://wa.me/966558096476"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.navLink}
          >
            {labels.contact}
          </a>
          {/* Divider */}
          <div className={styles.navDivider} />

          {/* Auth-dependent content */}
          <div
            className={styles.authTransitionWrapper}
            style={{
              opacity: isAuthVisible ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}
          >
            {!loading && (
              session ? (
                <AccountMenu menuItems={menuItems} language={language} />
              ) : (
                <>
                  <Link to="/login" className={styles.loginButton}>
                    {labels.login}
                  </Link>
                  <Link to="/signup" className={styles.ctaButton}>
                    {labels.getStarted}
                  </Link>
                </>
              )
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
